'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { History, Loader2, AlertCircle, CheckCircle2, Clock, Repeat, FileText, Send } from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import { authService } from '@/lib/authService';

interface RecurringJobInvocationHistoryTabProps {
  workItem: WorkItem;
  isDark?: boolean;
  isActive?: boolean;
}

interface InvocationHistoryEntry {
  id: string;
  state: string;
  occurrence_number?: number;
  document_id?: string;
  document_number?: string | null;
  document_type?: string;
  scheduled_date?: string | null;
  generated_at?: string | null;
  created_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  contact_name?: string | null;
  grand_total?: number | null;
  send_status?: string | null;
  sent_at?: string | null;
  sent_to?: string | null;
  send_error?: string | null;
}

interface InvocationHistoryResponse {
  success: boolean;
  data?: InvocationHistoryEntry[];
  error?: string;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
}

function formatCurrency(value?: number | null): string {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(value);
}

export default function RecurringJobInvocationHistoryTab({
  workItem,
  isDark = false,
  isActive = false,
}: RecurringJobInvocationHistoryTabProps) {
  const [entries, setEntries] = useState<InvocationHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    if (!isActive || !workItem.id) return;
    setLoading(true);
    setError(null);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch('/api/v1/work-items/recurring', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          action: 'invocation-history',
          id: workItem.id,
        }),
      });
      const result = (await response.json()) as InvocationHistoryResponse;
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load invocation history');
      }
      setEntries(result.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load invocation history';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [isActive, workItem.id]);

  useEffect(() => {
    if (isActive) {
      loadHistory();
    }
  }, [isActive, loadHistory]);

  const statusConfig = useMemo(() => {
    return {
      generated: { icon: CheckCircle2, color: 'text-emerald-500', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50', label: 'Generated' },
      sent: { icon: Send, color: 'text-blue-500', bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50', label: 'Sent' },
      failed: { icon: AlertCircle, color: 'text-red-500', bg: isDark ? 'bg-red-500/10' : 'bg-red-50', label: 'Failed' },
      pending: { icon: Clock, color: 'text-amber-500', bg: isDark ? 'bg-amber-500/10' : 'bg-amber-50', label: 'Pending' },
    } as const;
  }, [isDark]);

  const getStatusConfig = (entry: InvocationHistoryEntry) => {
    if (entry.send_status === 'failed') return statusConfig.failed;
    if (entry.send_status === 'sent' || entry.sent_at) return statusConfig.sent;
    if (entry.state === 'generated') return statusConfig.generated;
    return statusConfig.pending;
  };

  const cardCls = `rounded-xl border ${isDark ? 'bg-gray-900/60 border-gray-700' : 'bg-white border-gray-200'}`;
  const mutedCls = isDark ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className="space-y-4">
      <div className={`${cardCls} p-4`}>
        <div className="flex items-center gap-3">
          <div className={`rounded-lg p-2 ${isDark ? 'bg-violet-500/10' : 'bg-violet-50'}`}>
            <History className="h-5 w-5 text-violet-500" />
          </div>
          <div>
            <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Generation History
            </h3>
            <p className={`text-xs ${mutedCls}`}>
              Work items generated by this recurring schedule
            </p>
          </div>
          <button
            type="button"
            onClick={loadHistory}
            disabled={loading}
            className={`ml-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ${
              isDark
                ? 'bg-gray-800 text-gray-200 hover:bg-gray-700 disabled:opacity-50'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50'
            }`}
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Repeat className="h-3.5 w-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className={`${cardCls} p-4 border-l-4 border-l-red-500`}>
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Could not load history</p>
              <p className={`text-xs ${mutedCls} mt-0.5`}>{error}</p>
            </div>
          </div>
        </div>
      )}

      {loading && entries.length === 0 && (
        <div className={`${cardCls} p-8`}>
          <div className="flex flex-col items-center justify-center gap-3 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-violet-500" />
            <p className={`text-sm ${mutedCls}`}>Loading generation history…</p>
          </div>
        </div>
      )}

      {!loading && entries.length === 0 && !error && (
        <div className={`${cardCls} p-8`}>
          <div className="flex flex-col items-center justify-center gap-3 text-center">
            <History className={`h-8 w-8 ${mutedCls}`} />
            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>No generations yet</p>
            <p className={`text-xs ${mutedCls} max-w-sm`}>
              Generated work items will appear here once the schedule runs.
            </p>
          </div>
        </div>
      )}

      {entries.length > 0 && (
        <div className={`${cardCls} overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className={isDark ? 'bg-gray-800/60' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide ${mutedCls}`}>Status</th>
                  <th className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide ${mutedCls}`}>Occurrence</th>
                  <th className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide ${mutedCls}`}>Generated</th>
                  <th className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide ${mutedCls}`}>Document</th>
                  <th className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide ${mutedCls}`}>Contact</th>
                  <th className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide ${mutedCls}`}>Amount</th>
                  <th className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide ${mutedCls}`}>Send Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {entries.map((entry) => {
                  const config = getStatusConfig(entry);
                  const StatusIcon = config.icon;

                  return (
                    <tr key={entry.id} className={isDark ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-2">
                          <span className={`rounded-md p-1.5 ${config.bg}`}>
                            <StatusIcon className={`h-4 w-4 ${config.color}`} />
                          </span>
                          <span className={`text-xs font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                            {config.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          #{entry.occurrence_number ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{formatDate(entry.generated_at)}</span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        {entry.document_number ? (
                          <div className="flex items-center gap-1.5">
                            <FileText className={`h-3.5 w-3.5 ${mutedCls}`} />
                            <span className={`text-xs font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                              {entry.document_number}
                            </span>
                          </div>
                        ) : (
                          <span className={`text-xs ${mutedCls}`}>—</span>
                        )}
                        {entry.document_type && (
                          <p className={`text-xs ${mutedCls} mt-0.5`}>{entry.document_type}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {entry.contact_name || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {formatCurrency(entry.grand_total)}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        {entry.send_status ? (
                          <div className="flex items-center gap-1.5">
                            {entry.send_status === 'sent' ? (
                              <Send className="h-3.5 w-3.5 text-blue-500" />
                            ) : entry.send_status === 'failed' ? (
                              <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                            ) : (
                              <Clock className="h-3.5 w-3.5 text-amber-500" />
                            )}
                            <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                              {entry.send_status}
                            </span>
                            {entry.sent_to && (
                              <span className={`text-xs ${mutedCls}`}>to {entry.sent_to}</span>
                            )}
                          </div>
                        ) : (
                          <span className={`text-xs ${mutedCls}`}>—</span>
                        )}
                        {entry.send_error && (
                          <p className={`text-xs text-red-400 mt-0.5`}>{entry.send_error}</p>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
